import * as React from "react";
import "./AvatarEditor.component.css";
import { AvatarDisplay } from "../avatar-display/AvatarDisplay.component";
import Select from "react-select";
import { LabelledElement } from "../labelled-element/LabelledElement.component";
import { getReactSelectStyleClasses } from "../../style/style-functions";
import { COLOR_FLAVIA_BLUE, hexToNum, numToHex } from "../../style/predefined-colors";
import { ColorSelect } from "../color-select/ColorSelect.component";
import { useEffect, useState } from "react";
import { useAvatarConfigStore } from "../../store/avatar-config.store";
import { AvatarStyle } from "flavr-common";
import { PreferenceCheckbox } from "../preference-inputs/preference-checkbox/PreferenceCheckbox.component";
import { avatarPresets, getAvatarPresetIndex } from "./AvatarEditor.util";
import { HTMLTooltipWrapper } from "../html-tooltip/HTMLTooltipWrapper.component";
import { useTranslation } from "react-i18next";
export function AvatarEditor(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var { username = (_b = localStorage.getItem("username")) !== null && _b !== void 0 ? _b : "Guest" } = _a;
    const { t } = useTranslation();
    const avatarStore = useAvatarConfigStore();
    const [currentPreset, setCurrentPreset] = useState(getAvatarPresetIndex(avatarStore.avatar));
    const [currentBaseColor, setCurrentBaseColor] = useState((_c = numToHex(avatarStore.avatar.baseColor)) !== null && _c !== void 0 ? _c : COLOR_FLAVIA_BLUE);
    const [currentHeadgear, setCurrentHeadgear] = useState((_e = (_d = avatarStore.avatar.avatarHat) === null || _d === void 0 ? void 0 : _d.id) !== null && _e !== void 0 ? _e : -1);
    const [currentHeadgearColor, setCurrentHeadgearColor] = useState(avatarStore.avatar.avatarHat ? numToHex(avatarStore.avatar.avatarHat.color) : undefined);
    const [currentClothing, setCurrentClothing] = useState((_g = (_f = avatarStore.avatar.avatarShoulder) === null || _f === void 0 ? void 0 : _f.id) !== null && _g !== void 0 ? _g : -1);
    const [currentClothingColor, setCurrentClothingColor] = useState(avatarStore.avatar.avatarShoulder ? numToHex(avatarStore.avatar.avatarShoulder.color) : undefined);
    const createPresetOptions = () => {
        const presetsOptions = avatarPresets().map((preset, index) => ({ value: index, label: preset.name }));
        if (currentPreset === -1) {
            presetsOptions.unshift({ value: -1, label: t("settings:avatar-editor-preset-option-custom", "Custom") });
        }
        return presetsOptions;
    };
    const onPresetChanged = (option) => {
        var _a;
        setCurrentPreset((_a = option === null || option === void 0 ? void 0 : option.value) !== null && _a !== void 0 ? _a : -1);
        if (!option || option.value === -1) {
            return;
        }
        const preset = avatarPresets()[option.value];
        setCurrentBaseColor(preset.baseColor);
        setCurrentHeadgear(preset.headGear.id);
        setCurrentHeadgearColor(preset.headGear.color);
        setCurrentClothing(preset.clothing.id);
        setCurrentClothingColor(preset.clothing.color);
    };
    const onBaseColorChanged = (color) => {
        if (currentPreset >= 0 && avatarPresets()[currentPreset].baseColor !== color) {
            setCurrentPreset(-1);
        }
        avatarStore.setBaseColor(hexToNum(color));
    };
    useEffect(() => {
        avatarStore.setBaseColor(hexToNum(currentBaseColor));
    }, [currentBaseColor]);
    const createHeadgearOptions = () => {
        return [
            { value: -1, label: t("settings:avatar-editor-headgear-option-none", "None") },
            { value: 4, label: t("settings:avatar-editor-headgear-option-helmet", "Helmet") },
            { value: 5, label: t("settings:avatar-editor-headgear-option-carpenter-hat", "Carpenter hat") },
            { value: 6, label: t("settings:avatar-editor-headgear-option-ear-protector", "Ear protectors") },
            { value: 7, label: t("settings:avatar-editor-headgear-option-alu-hat", "Aluminium Hat") },
            { value: 8, label: t("settings:avatar-editor-headgear-option-baseball-cap", "Baseball Cap") },
            { value: 12, label: t("settings:avatar-editor-headgear-option-bowler-hat", "Bowler Hat") },
            { value: 13, label: t("settings:avatar-editor-headgear-option-cylinder", "Cylinder") },
            { value: 14, label: t("settings:avatar-editor-headgear-option-asian-hat", "Asian Hat") }
        ];
    };
    const onHeadgearChanged = (option) => {
        var _a;
        if (currentPreset >= 0 && avatarPresets()[currentPreset].headGear.id !== (option === null || option === void 0 ? void 0 : option.value)) {
            setCurrentPreset(-1);
        }
        setCurrentHeadgear((_a = option === null || option === void 0 ? void 0 : option.value) !== null && _a !== void 0 ? _a : -1);
    };
    const onHeadgearColorChanged = (color) => {
        if (currentPreset >= 0 && avatarPresets()[currentPreset].headGear.color !== color) {
            setCurrentPreset(-1);
        }
        setCurrentHeadgearColor(color);
    };
    useEffect(() => {
        if (currentHeadgear !== undefined && currentHeadgearColor !== undefined) {
            let hat;
            if (currentHeadgear > -1) {
                hat = { id: currentHeadgear, color: hexToNum(currentHeadgearColor) };
            }
            avatarStore.setHat(hat);
        }
    }, [currentHeadgear, currentHeadgearColor]);
    const createClothingOptions = () => {
        return [
            { value: -1, label: t("settings:avatar-editor-clothing-option-none", "None") },
            { value: 0, label: t("settings:avatar-editor-clothing-option-bowtie", "Bowtie") },
            { value: 1, label: t("settings:avatar-editor-clothing-option-braces", "Braces") },
            { value: 2, label: t("settings:avatar-editor-clothing-option-suit", "Suit") },
            { value: 3, label: t("settings:avatar-editor-clothing-option-tie", "Tie") },
            { value: 18, label: t("settings:avatar-editor-clothing-option-scarf", "Scarf") }
        ];
    };
    const onClothingChanged = (option) => {
        var _a;
        if (currentPreset >= 0 && avatarPresets()[currentPreset].clothing.id !== (option === null || option === void 0 ? void 0 : option.value)) {
            setCurrentPreset(-1);
        }
        setCurrentClothing((_a = option === null || option === void 0 ? void 0 : option.value) !== null && _a !== void 0 ? _a : -1);
    };
    const onClothingColorChanged = (color) => {
        if (currentPreset >= 0 && avatarPresets()[currentPreset].clothing.color !== color) {
            setCurrentPreset(-1);
        }
        setCurrentClothingColor(color);
    };
    useEffect(() => {
        if (currentClothing !== undefined && currentClothingColor !== undefined) {
            let shoulder;
            if (currentClothing > -1) {
                shoulder = { id: currentClothing, color: hexToNum(currentClothingColor) };
            }
            avatarStore.setShoulder(shoulder);
        }
    }, [currentClothing, currentClothingColor]);
    return (React.createElement("section", { className: "settings-outer-container" },
        React.createElement("div", { className: "avatar-editor-container settings-container" },
            React.createElement("div", { className: "avatar-editor-avatar-preview-container" },
                React.createElement(AvatarDisplay, { username: username }),
                React.createElement("div", { className: "avatar-editor-avatar-preview-checkbox", "data-tooltip": t("settings:avatar-editor-checkbox-preview-vr-tooltip", "Set this option to see how you will look like while in VR Mode") },
                    React.createElement(PreferenceCheckbox, { label: t("settings:avatar-editor-checkbox-preview-vr", "Preview VR Style"), preferenceName: "showAvatarVRStyle", onChanged: (checked) => {
                            avatarStore.setStyle(checked ? AvatarStyle.Head : AvatarStyle.Monitor);
                        } }))),
            React.createElement(HTMLTooltipWrapper, { tooltip: t("settings:avatar-editor-select-preset-tooltip", "Choose a preset to quickly change your avatar's appearance\nCaution: You will loose all custom changes you made when selecting a preset") },
                React.createElement(LabelledElement, { label: t("settings:avatar-editor-select-preset", "Preset") },
                    React.createElement(Select, { onChange: onPresetChanged, value: (_h = createPresetOptions().find((o) => o.value === currentPreset)) !== null && _h !== void 0 ? _h : undefined, options: createPresetOptions(), classNames: getReactSelectStyleClasses(), menuPlacement: "auto", menuPortalTarget: document.body }))),
            React.createElement("div", { className: "settings-empty-row" }),
            React.createElement(ColorSelect, { label: t("settings:avatar-editor-select-base-color", "Base color"), color: currentBaseColor !== null && currentBaseColor !== void 0 ? currentBaseColor : COLOR_FLAVIA_BLUE, onColorChanged: onBaseColorChanged }),
            React.createElement("div", { className: "settings-empty-row" }),
            React.createElement("div", { className: "settings-responsive-row" },
                React.createElement(LabelledElement, { label: t("settings:avatar-editor-select-headgear", "Headgear") },
                    React.createElement(Select, { onChange: onHeadgearChanged, value: (_j = createHeadgearOptions().find((o) => o.value === currentHeadgear)) !== null && _j !== void 0 ? _j : undefined, options: createHeadgearOptions(), classNames: getReactSelectStyleClasses(), menuPlacement: "auto", menuPortalTarget: document.body })),
                React.createElement("div", { className: "settings-responsive-row-spacer" }),
                React.createElement(HTMLTooltipWrapper, { tooltipDisabled: t("settings:avatar-editor-select-headgear-color-tooltip-disabled", "Please choose a headgear first") },
                    React.createElement(ColorSelect, { label: t("settings:avatar-editor-select-headgear-color", "Headgear color"), color: currentHeadgearColor !== null && currentHeadgearColor !== void 0 ? currentHeadgearColor : COLOR_FLAVIA_BLUE, disabled: currentHeadgear === -1, onColorChanged: onHeadgearColorChanged }))),
            React.createElement("div", { className: "settings-responsive-row" },
                React.createElement(LabelledElement, { label: t("settings:avatar-editor-select-clothing", "Clothing") },
                    React.createElement(Select, { onChange: onClothingChanged, value: (_k = createClothingOptions().find((o) => o.value === currentClothing)) !== null && _k !== void 0 ? _k : undefined, options: createClothingOptions(), classNames: getReactSelectStyleClasses(), menuPlacement: "auto", menuPortalTarget: document.body })),
                React.createElement("div", { className: "settings-responsive-row-spacer" }),
                React.createElement(HTMLTooltipWrapper, { tooltipDisabled: t("settings:avatar-editor-select-clothing-color-tooltip-disabled", "Please choose a clothing first") },
                    React.createElement(ColorSelect, { label: t("settings:avatar-editor-select-clothing-color", "Clothing color"), color: currentClothingColor !== null && currentClothingColor !== void 0 ? currentClothingColor : COLOR_FLAVIA_BLUE, disabled: currentClothing === -1, onColorChanged: onClothingColorChanged }))))));
}
